// frontend/src/home/clip/ClipTable.js

import * as React from 'react';
import { Box, Grid, Typography, Fade, Pagination, Stack } from '@mui/material';
import { context } from '../context';
import { ClipCard } from './ClipCard';

// 辅助函数：从URL中提取Bilibili的BV号
const extractBvId = (url) => {
    if (!url || typeof url !== 'string') return null;
    const match = url.match(/BV[a-zA-Z0-9]+/);
    return match ? match[0] : null;
};

export const ClipTable = () => {
    const { selectedAuthors, clips, currentPage, totalPages, totalClips, onPageChange } = React.useContext(context);

    // 将 clips 按 BV 号进行分组（不再前端过滤作者，后端已按组织过滤）
    const groupedClips = React.useMemo(() => {
        const groups = new Map();
        
        for (const clip of clips) {
            // 只对B站源(type=1)的视频进行分组
            if (clip.type === 1) {
                const bvId = clip.bv || extractBvId(clip.redirectUrl);
                if (bvId) {
                    if (!groups.has(bvId)) {
                        groups.set(bvId, []);
                    }
                    groups.get(bvId).push(clip);
                    continue; // B站源已处理，进入下一次循环
                }
            }
            // 对于非B站源或无法提取BV号的视频，每个视频自成一组
            groups.set(`clip-${clip.id}`, [clip]);
        }
        
        // 将Map的值转为数组，并对每个分组按 part 排序
        return Array.from(groups.values()).map(group => {
            // 按 part 升序排列，确保 part=1 在第一位
            return group.sort((a, b) => (a.part || 1) - (b.part || 1));
        });
    }, [clips]);

    // 空状态
    if (groupedClips.length === 0 && selectedAuthors.length > 0) {
        return (
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                justifyContent: 'center',
                py: 10,
                px: 3,
            }}>
                <Box 
                    sx={{ 
                        width: 64,
                        height: 64,
                        borderRadius: '16px',
                        bgcolor: 'rgba(255, 139, 147, 0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                    }}
                >
                    <Typography sx={{ fontSize: '1.5rem' }}>📭</Typography>
                </Box>
                <Typography 
                    variant="body1" 
                    sx={{ 
                        color: 'text.secondary',
                        textAlign: 'center',
                    }}
                >
                    暂无匹配的视频
                </Typography>
                <Typography 
                    variant="caption" 
                    sx={{ 
                        color: 'text.disabled',
                        mt: 0.5,
                    }}
                >
                    尝试调整筛选条件
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            {/* 结果统计 */}
            {totalClips > 0 && (
                <Typography 
                    variant="body2" 
                    sx={{ 
                        color: 'text.secondary', 
                        mb: 2,
                        fontSize: '0.875rem'
                    }}
                >
                    共 {totalClips} 个结果
                </Typography>
            )}

            <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
                {groupedClips.map((group, index) => {
                    // 使用每个分组的第一个clip作为卡片代表
                    const representativeClip = group[0];
                    return (
                        <Fade 
                            in={true} 
                            timeout={300 + index * 50}
                            key={representativeClip.id}
                        >
                            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                                {/* 将代表clip和整个分组(group)都传给ClipCard */}
                                <ClipCard clip={representativeClip} clipGroup={group} />
                            </Grid>
                        </Fade>
                    );
                })}
            </Grid>

            {/* 分页控件 */}
            {totalPages > 1 && (
                <Stack 
                    direction="row" 
                    justifyContent="center" 
                    sx={{ mt: 4, mb: 2 }}
                >
                    <Pagination 
                        count={totalPages}
                        page={currentPage}
                        onChange={onPageChange}
                        color="primary"
                        size="large"
                        showFirstButton
                        showLastButton
                        sx={{
                            '& .MuiPaginationItem-root': {
                                borderRadius: '8px',
                            }
                        }}
                    />
                </Stack>
            )}
        </Box>
    );
}